import React, { useState, useEffect } from 'react';
import { ReportDetailsFormData } from './types';
import { AlertCircle } from 'lucide-react';
import { useInputValidation } from '../../../utils/validation/inputValidation';
import { REPORT_FORM_VALIDATION } from './constants';

interface ReportDetailsFormProps {
  formData: ReportDetailsFormData;
  onChange: (field: keyof ReportDetailsFormData, value: string) => void;
  isAdmin: boolean;
  salesContacts: Array<{
    email: string;
    name: string;
    role?: string;
    phone?: string;
    photo_url?: string;
  }>;
  projectName?: string; // Add project name prop
}

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const ReportDetailsForm: React.FC<ReportDetailsFormProps> = ({
  formData,
  onChange,
  projectName,
}) => {
  // Create validation hooks for each field
  const titleValidation = useInputValidation(
    formData.title,
    REPORT_FORM_VALIDATION.title
  );
  
  const nameValidation = useInputValidation(
    formData.authorName,
    REPORT_FORM_VALIDATION.authorName
  );
  
  const roleValidation = useInputValidation(
    formData.authorRole,
    REPORT_FORM_VALIDATION.authorRole
  );
  
  const emailValidation = useInputValidation(
    formData.authorEmail,
    REPORT_FORM_VALIDATION.email
  );

  // Update validation hooks when form data changes from parent
  useEffect(() => {
    titleValidation.setValue(formData.title);
  }, [formData.title, titleValidation]);
  
  useEffect(() => {
    nameValidation.setValue(formData.authorName);
  }, [formData.authorName, nameValidation]);
  
  useEffect(() => {
    roleValidation.setValue(formData.authorRole);
  }, [formData.authorRole, roleValidation]);
  
  useEffect(() => {
    emailValidation.setValue(formData.authorEmail);
    // Clear email error when email is updated from parent
    if (formData.authorEmail && EMAIL_REGEX.test(formData.authorEmail)) {
      setEmailError("");
    }
  }, [formData.authorEmail, emailValidation]);

  // Custom email validation
  const [emailError, setEmailError] = useState("");
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emailValidation.handleChange(e);
    
    // Clear previous error
    setEmailError("");
    
    // Check email format if not empty
    if (e.target.value && !EMAIL_REGEX.test(e.target.value)) {
      setEmailError("Please enter a valid email address");
    }
    
    onChange('authorEmail', e.target.value);
  };
  
  const handleEmailBlur = () => {
    emailValidation.handleBlur();
    
    // Check email format if not empty
    if (formData.authorEmail && !EMAIL_REGEX.test(formData.authorEmail)) {
      setEmailError("Please enter a valid email address");
    }
  };
  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="mb-4">
        <div className="flex justify-between">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Report Title <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {titleValidation.value.length}/{REPORT_FORM_VALIDATION.title.maxLength}
          </span>
        </div>
        <input
          type="text"
          value={titleValidation.value}
          onChange={(e) => {
            // If the input is being cleared and we have a project name, use the project name
            const newValue = e.target.value.trim() === "" && projectName ? projectName : e.target.value;
            titleValidation.handleChange({ ...e, target: { ...e.target, value: newValue } });
            onChange('title', newValue);
          }}
          onBlur={titleValidation.handleBlur}
          placeholder="Report Title"
          maxLength={REPORT_FORM_VALIDATION.title.maxLength}
          required
          className={`w-full px-3 py-2 border ${
            titleValidation.errorMessage 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
          } rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {titleValidation.errorMessage && (
          <div className="mt-1 flex items-center text-red-500 text-xs">
            <AlertCircle className="h-3 w-3 mr-1" />
            {titleValidation.errorMessage}
          </div>
        )}
      </div>

      {/* Author Name */}
      <div className="mb-4">
        <div className="flex justify-between">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <span className="text-xs text-gray-500">
            {nameValidation.value.length}/{REPORT_FORM_VALIDATION.authorName.maxLength}
          </span>
        </div>
        <input
          type="text"
          value={nameValidation.value}
          onChange={(e) => {
            nameValidation.handleChange(e);
            onChange('authorName', e.target.value);
          }}
          onBlur={nameValidation.handleBlur}
          placeholder="Your Name"
          maxLength={REPORT_FORM_VALIDATION.authorName.maxLength}
          className={`w-full px-3 py-2 border ${
            nameValidation.errorMessage 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
          } rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {nameValidation.errorMessage && (
          <div className="mt-1 flex items-center text-red-500 text-xs">
            <AlertCircle className="h-3 w-3 mr-1" />
            {nameValidation.errorMessage}
          </div>
        )}
      </div>

      {/* Author Role */}
      <div className="mb-4">
        <div className="flex justify-between">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Role
          </label>
          <span className="text-xs text-gray-500">
            {roleValidation.value.length}/{REPORT_FORM_VALIDATION.authorRole.maxLength}
          </span>
        </div>
        <input
          type="text"
          value={roleValidation.value}
          onChange={(e) => {
            roleValidation.handleChange(e);
            onChange('authorRole', e.target.value);
          }}
          onBlur={roleValidation.handleBlur}
          placeholder="Your Role or Title"
          maxLength={REPORT_FORM_VALIDATION.authorRole.maxLength}
          className={`w-full px-3 py-2 border ${
            roleValidation.errorMessage 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
          } rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {roleValidation.errorMessage && (
          <div className="mt-1 flex items-center text-red-500 text-xs">
            <AlertCircle className="h-3 w-3 mr-1" />
            {roleValidation.errorMessage}
          </div>
        )}
      </div>

      {/* Author Email */}
      <div>
        <div className="flex justify-between">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {emailValidation.value.length}/{REPORT_FORM_VALIDATION.email.maxLength}
          </span>
        </div>
        <input
          type="email"
          value={emailValidation.value}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          placeholder="your@email.com"
          maxLength={REPORT_FORM_VALIDATION.email.maxLength}
          required
          className={`w-full px-3 py-2 border ${
            (emailValidation.errorMessage || emailError) 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
          } rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {(emailValidation.errorMessage || emailError) && (
          <div className="mt-1 flex items-center text-red-500 text-xs">
            <AlertCircle className="h-3 w-3 mr-1" />
            {emailError || emailValidation.errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};
